import { LoadingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Flex, Form, Modal, Radio, Space, Switch, Typography } from "antd";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { SeasonStatuses } from "~enums/seasonStatuses";
import type { UserRoles } from "~enums/userRoles";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useGetPermissionUser } from "~pages/Users/hooks";
import { usePermissionUserService } from "~pages/Users/hooks/usePermissionUserService.ts";
import type { LabelValue } from "~types/labelValue";
import { DEFAULT_ROLE_CONFIG, userRolesConfig } from "../constants/userRolesConfig";
import { PermissionFields } from "../enums/permissionFields";
import { PermissionScopeTypes } from "../enums/permissionScopeTypes";
import type { UserPermissionTransformed, UserRoleConfig } from "../types";
import { ConfirmationText, PermissionFieldCheckboxGroup, SelectPermissionScope, SelectUserRole } from "./components";
import { SELECT_ALL_VALUE } from "./constants/ui";
import { useGetCurrentUserPermissionScopeToManagePermissions } from "./hooks";
import type { PermissionScopeTreeNode, UpsertUserPermissionData, UserPermissionForm } from "./types";
import {
  findHighestRoleToManagePermissions,
  getFormInitialValues,
  transformEntityGroupScopeTree,
  transformFormFieldValue,
  transformScopeLeagueTree,
} from "./utilities";

import "./PermissionUserManagePage.scss";

const FieldLabel = ({ label, description, required }: { label: ReactNode; description: ReactNode; required: boolean }) => (
  <>
    <span className="field-label">{label}</span>
    {required && <span className="label-mark">*</span>}
    <br />
    <span className="field-description">{description}</span>
  </>
);

const PermissionUserManagePage = () => {
  const { data: user, isLoading: isUserLoading } = useGetPermissionUser();
  const { customerId, sport, organizationId, userId, permissionId } = useParams();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [modal, modalContextHolder] = Modal.useModal();
  const isEditMode = !!permissionId;
  const userPermission = useMemo<undefined | UserPermissionTransformed>(() => {
    if (isUserLoading || !permissionId) {
      return;
    }
    return user?.permissions?.find((permission: UserPermissionTransformed) => Number(permissionId) === permission.permissionId);
  }, [isUserLoading, permissionId, user]);

  const upsertRoleStr = formatMessage({ id: `permissions.user.role.actions.${isEditMode ? "edit" : "add"}.title` });
  const fullTitle = `${upsertRoleStr} - ${user?.name || ""}`;

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [resetAfterSubmit, setResetAfterSubmit] = useState(false);
  const { fixtureStatuses } = useSportConfiguration();
  const { userRoles } = useUserPermissions();
  const { upsertUserPermission, isUpsertPending: isUpsertInProgress } = usePermissionUserService();
  const selectAllOption: LabelValue = useMemo(() => ({ label: formatMessage({ id: "all" }), value: SELECT_ALL_VALUE }), [formatMessage]);

  const currentUserRole = useMemo(() => findHighestRoleToManagePermissions(userRoles), [userRoles]);

  const userRoleOptions = useMemo<LabelValue[]>(
    () =>
      userRolesConfig[currentUserRole]?.canAssignRoles?.map((userRole: UserRoles) => ({
        value: userRole,
        label: formatMessage({ id: `permissions.userRole.${userRole}` }),
      })),
    [currentUserRole, formatMessage],
  );

  const [form] = Form.useForm<UserPermissionForm>();
  const {
    userRole: selectedUserRole = userPermission?.userRole,
    scope: selectedScope,
    scopeValues: selectedScopeValues,
  } = Form.useWatch([], form) || {};

  const { requiredFields, disabledFields, allowedScopeTypes, advancedSettingsEnabled } = useMemo<UserRoleConfig>(
    () => (selectedUserRole ? userRolesConfig[selectedUserRole] : DEFAULT_ROLE_CONFIG),
    [selectedUserRole],
  );

  const { data: scopeData, isLoading: isScopeLoading } = useGetCurrentUserPermissionScopeToManagePermissions();
  const { scope, resources = {} } = scopeData || {};
  const scopeTreeHasNoLeagues = scope?.leagueTree?.length === 1 && !scope.leagueTree[0].leagueId;

  const formattedLeagueTree = useMemo<PermissionScopeTreeNode[]>(() => {
    if (!selectedScope || selectedScope === SELECT_ALL_VALUE) {
      return [];
    }

    return transformScopeLeagueTree(scope?.leagueTree || [], resources || {}, selectedScope as PermissionScopeTypes);
  }, [scope, selectedScope, scope?.leagueTree, resources]);

  const { entities: entityOptions, entityGroups: entityGroupOptions } = transformEntityGroupScopeTree(
    scope?.entityGroupTree || [],
    resources || {},
  );

  const scopeLevelOptions = useMemo<LabelValue[]>(() => {
    let options: LabelValue[] = Object.values(PermissionScopeTypes).map((value) => ({
      value,
      label: formatMessage({ id: value.toLowerCase() }),
      disabled: !allowedScopeTypes.has(value),
    }));

    if (scopeTreeHasNoLeagues) {
      options = options.filter(({ value }) => value !== PermissionScopeTypes.League);
    }

    if (scope?.isFullScope) {
      options.unshift(selectAllOption);
    }

    return options;
  }, [scope?.isFullScope, allowedScopeTypes, selectAllOption, formatMessage, scopeTreeHasNoLeagues]);

  const scopeSeasonStatusesOptions = useMemo<LabelValue[] | undefined>(
    () =>
      scope?.seasonStatuses?.map((status) => ({
        label: status,
        value: status,
      })),
    [scope?.seasonStatuses],
  );
  const scopeFixtureStatusesOptions = useMemo<LabelValue[] | undefined>(
    () =>
      scope?.fixtureStatuses?.map((status) => ({
        label: status,
        value: status,
      })),
    [scope?.fixtureStatuses],
  );

  const fixtureStatusesOptions = useMemo<LabelValue[]>(() => {
    if (!scopeFixtureStatusesOptions) {
      return [selectAllOption, ...fixtureStatuses.map((status) => ({ label: status, value: status }))];
    }
    return scopeFixtureStatusesOptions;
  }, [fixtureStatuses, scopeFixtureStatusesOptions, selectAllOption]);

  const seasonStatusesOptions = useMemo<LabelValue[]>(() => {
    if (!scopeSeasonStatusesOptions) {
      return [selectAllOption, ...Object.values(SeasonStatuses).map((status) => ({ label: status, value: status }))];
    }
    return scopeSeasonStatusesOptions;
  }, [scopeSeasonStatusesOptions, selectAllOption]);

  const initialValues = useMemo<UserPermissionForm>(
    () => getFormInitialValues(userPermission, scope, fixtureStatusesOptions, seasonStatusesOptions),
    [userPermission, scope, fixtureStatusesOptions, seasonStatusesOptions],
  );

  const handleRoleChange = () =>
    form.setFieldsValue({
      [PermissionFields.Scope]: scope?.isFullScope ? SELECT_ALL_VALUE : undefined,
      [PermissionFields.ScopeValues]: undefined,
      [PermissionFields.SeasonStatuses]: seasonStatusesOptions.map(({ value }) => value),
      [PermissionFields.FixtureStatuses]: fixtureStatusesOptions.map(({ value }) => value),
    });

  const handleSubmit = (values: UserPermissionForm) => {
    const upsertValues: UpsertUserPermissionData = {
      permissionId: userPermission?.permissionId,
      upsertData: {
        userRole: values.userRole as UserRoles,
        scope: (values.scope === SELECT_ALL_VALUE ? null : values.scope) as PermissionScopeTypes,
        scopeIds: values.scopeValues?.map(({ value }) => value) || null,
        seasonStatuses: transformFormFieldValue(values.seasonStatuses),
        fixtureStatuses: transformFormFieldValue(values.fixtureStatuses),
        roleTypes: null,
      },
    };

    return upsertUserPermission(upsertValues).then((result) => {
      if (!result) {
        return;
      }

      if (resetAfterSubmit) {
        form.resetFields();
        setResetAfterSubmit(false);
        setShowAdvancedSettings(false);
      } else {
        navigate(`/${customerId}/admin/${sport}/${organizationId}/permissions/manage/${userId}`, { state: { userRole: selectedUserRole } });
      }
    });
  };

  const onSubmitClick = (values: UserPermissionForm) =>
    modal.confirm({
      title: formatMessage({ id: `permissions.user.role.actions.${isEditMode ? "edit" : "add"}.title` }),
      content: (
        <ConfirmationText
          role={selectedUserRole as UserRoles}
          userName={user?.name}
          scope={selectedScope}
          scopeValues={selectedScopeValues}
        />
      ),
      okText: formatMessage({ id: "confirm" }),
      cancelText: formatMessage({ id: "cancel" }),
      onOk: async () => handleSubmit(values),
      centered: true,
      width: 800,
    });

  const isGlobalDisabled = isUpsertInProgress || isScopeLoading || !selectedUserRole;
  const isScopeValuesSelectorHidden = !selectedScope || selectedScope === SELECT_ALL_VALUE;

  const requiredRule = {
    required: true,
    message: formatMessage({ id: "form.help.required.field.generic" }),
  };

  const goBackToUser = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/permissions/manage/${userId}`);

  // biome-ignore lint/correctness/useExhaustiveDependencies: this hook should run on properties change in scopeLevelOptions
  useEffect(() => {
    if (scopeLevelOptions.filter((opt) => !opt.disabled).length === 1) {
      form.setFieldValue(PermissionFields.Scope, scopeLevelOptions.find((opt) => !opt.disabled)?.value);
    }
  }, [form, JSON.stringify(scopeLevelOptions)]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: this hook should re-run after initial values are loaded from BE
  useEffect(() => {
    form.resetFields();
  }, [initialValues]);

  if (isUserLoading) {
    return (
      <div className="loading-full-screen">
        <LoadingOutlined style={{ fontSize: 44 }} spin />
      </div>
    );
  }

  return (
    <Page title={fullTitle} subtitle={formatMessage({ id: "permissions.user.role.configure" })} onBack={goBackToUser}>
      {modalContextHolder}
      <Form
        requiredMark={(label: ReactNode) => label}
        form={form}
        initialValues={initialValues}
        clearOnDestroy
        onFinish={onSubmitClick}
        layout="vertical"
        className="manage-user-permission-form"
      >
        <Form.Item<UserPermissionForm>
          name="userRole"
          label={
            <FieldLabel
              label={formatMessage({ id: "permissions.user.role" })}
              description={formatMessage({ id: "form.help.permissions.role.userRole" })}
              required
            />
          }
          rules={[{ ...requiredRule }]}
        >
          <SelectUserRole options={userRoleOptions} disabled={isUpsertInProgress} onChange={handleRoleChange} />
        </Form.Item>

        <Form.Item<UserPermissionForm>
          name={PermissionFields.Scope}
          label={
            <FieldLabel
              label={formatMessage({ id: "permissions.user.role.scope" })}
              description={formatMessage({ id: "form.help.permissions.role.scope" })}
              required={!!selectedUserRole && requiredFields.has(PermissionFields.Scope)}
            />
          }
          rules={!disabledFields.has(PermissionFields.Scope) ? [{ ...requiredRule }] : []}
        >
          <Radio.Group
            block
            size="large"
            disabled={isUpsertInProgress || !selectedUserRole || disabledFields.has(PermissionFields.Scope)}
            options={scopeLevelOptions}
            onChange={() => {
              form.setFieldsValue({
                [PermissionFields.ScopeValues]: undefined,
              });
            }}
          />
        </Form.Item>

        <Form.Item<UserPermissionForm>
          name={PermissionFields.ScopeValues}
          label={
            <FieldLabel
              label={selectedScope ? formatMessage({ id: selectedScope.toLowerCase() }) : ""}
              description={formatMessage(
                { id: "form.help.permissions.role.scopeValues" },
                { scopeType: selectedScope ? formatMessage({ id: selectedScope.toLowerCase() }) : "" },
              )}
              required={requiredFields.has(PermissionFields.Scope) && selectedScope !== SELECT_ALL_VALUE}
            />
          }
          hidden={isScopeValuesSelectorHidden}
          rules={requiredFields.has(PermissionFields.Scope) && selectedScope !== SELECT_ALL_VALUE ? [{ ...requiredRule }] : []}
        >
          <SelectPermissionScope
            placeholder={requiredFields.has(PermissionFields.Scope) ? "" : formatMessage({ id: "all" })}
            allowedScope={selectedScope}
            leagueTreeData={formattedLeagueTree}
            entityOptions={entityOptions}
            entityGroupOptions={entityGroupOptions}
            disabled={isGlobalDisabled}
            isLoading={isScopeLoading}
            onChange={(value) => form.setFieldValue(PermissionFields.ScopeValues, value)}
          />
        </Form.Item>

        {advancedSettingsEnabled && (
          <Flex align="center" gap={8} className="advanced-settings-wrapper">
            <Switch size="small" value={showAdvancedSettings} onChange={setShowAdvancedSettings} />
            <Typography.Text>
              <FormattedMessage id="settings.advanced" />
            </Typography.Text>
          </Flex>
        )}

        <div style={{ display: advancedSettingsEnabled && showAdvancedSettings ? "block" : "none", marginBottom: "1rem" }}>
          <Form.Item<UserPermissionForm>
            name={PermissionFields.SeasonStatuses}
            label={
              <FieldLabel
                label={formatMessage({ id: "season.statuses" })}
                description={formatMessage({ id: "form.help.permissions.role.seasonStatuses" })}
                required={requiredFields.has(PermissionFields.SeasonStatuses)}
              />
            }
            rules={requiredFields.has(PermissionFields.SeasonStatuses) ? [{ ...requiredRule }] : []}
          >
            <PermissionFieldCheckboxGroup
              options={seasonStatusesOptions}
              disabled={isGlobalDisabled || disabledFields.has(PermissionFields.SeasonStatuses)}
              onChange={(value) => form.setFieldValue(PermissionFields.SeasonStatuses, value)}
            />
          </Form.Item>

          <Form.Item<UserPermissionForm>
            name={PermissionFields.FixtureStatuses}
            label={
              <FieldLabel
                label={formatMessage({ id: "fixture.statuses" })}
                description={formatMessage({ id: "form.help.permissions.role.fixtureStatuses" })}
                required={requiredFields.has(PermissionFields.FixtureStatuses)}
              />
            }
            rules={requiredFields.has(PermissionFields.FixtureStatuses) ? [{ ...requiredRule }] : []}
          >
            <PermissionFieldCheckboxGroup
              options={fixtureStatusesOptions}
              disabled={isGlobalDisabled || disabledFields.has(PermissionFields.FixtureStatuses)}
              onChange={(value) => form.setFieldValue(PermissionFields.FixtureStatuses, value)}
            />
          </Form.Item>
        </div>
        {!isEditMode && (
          <Checkbox checked={resetAfterSubmit} onChange={(e) => setResetAfterSubmit(e.target.checked)}>
            <FormattedMessage id="permissions.user.role.actions.add.another" />
          </Checkbox>
        )}
        <div className="action-buttons-wrapper">
          <Space>
            <Button type="primary" htmlType="submit">
              <FormattedMessage id={`permissions.user.role.actions.${isEditMode ? "edit" : "add"}.title`} />
            </Button>
            <Button htmlType="button" onClick={goBackToUser}>
              <FormattedMessage id="cancel" />
            </Button>
          </Space>
        </div>
      </Form>
    </Page>
  );
};

export default PermissionUserManagePage;
